import {
  type DefaultMutationHook,
  forbiddenOverride,
  type MutationHookConstraint,
  type PrefixFn,
  useShowMutationError,
} from '../../hooks/use-show-mutation-error.tsx'

/** The refusal text, shared with the check that recognises its toast. */
export const REFUSED_CHANGE_MESSAGE = "You can't make this change. Reload the page to see what you can do."

/**
 * The read-only notice, as a {@link useShowMutationError} override.
 *
 * A 403 on a project or membership change means the permission went away mid-session: the control
 * that started the change was offered because the response carried the operation's template, and the
 * server has since disagreed. The spec asks every such refusal to show the read-only notice rather than
 * the generic failure toast, and `forbiddenOverride` makes it sticky for the same reason every other
 * admin-gated write does — waiting will not restore a permission.
 */
const ADMIN_REQUIRED_OVERRIDE = {
  403: forbiddenOverride(REFUSED_CHANGE_MESSAGE),
}

/**
 * The error toast for a project or membership write: the caller's prefix, and the read-only notice on
 * a 403. Every such write wants both, and six call sites spelling out the same pair is six chances to
 * leave the override off one of them.
 */
export const useShowProjectMutationError = <TMutation extends MutationHookConstraint = DefaultMutationHook>(
  prefix: string | PrefixFn<TMutation>
) => useShowMutationError<TMutation>({ prefix, overrides: ADMIN_REQUIRED_OVERRIDE })
